import React, { createContext, useState, useEffect, useContext } from 'react';

// Theme modes
export const THEME_MODE = {
  SYSTEM: 'system',
  LIGHT: 'light',
  DARK: 'dark'
};

// Create context
const ThemeContext = createContext();

// Custom hook to use the theme context
export const useTheme = () => {
  return useContext(ThemeContext);
};

// Provider component
export const ThemeProvider = ({ children }) => {
  // Get the initial theme mode from localStorage or default to system
  const [themeMode, setThemeMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || THEME_MODE.SYSTEM;
  });

  // State to track if dark mode is active (based on theme mode and system preference)
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Effect to detect system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    // Function to update theme based on system preference and user setting
    const updateTheme = () => {
      const systemPrefersDark = mediaQuery.matches;
      const shouldUseDark = 
        themeMode === THEME_MODE.DARK || 
        (themeMode === THEME_MODE.SYSTEM && systemPrefersDark);
      
      setIsDarkMode(shouldUseDark);
      
      // Update the document's data-bs-theme attribute
      document.documentElement.setAttribute('data-bs-theme', shouldUseDark ? 'dark' : 'light');
      document.body.setAttribute('data-bs-theme', shouldUseDark ? 'dark' : 'light');
      
      // Add or remove a class to the body for additional styling if needed
      if (shouldUseDark) {
        document.body.classList.add('dark-mode');
      } else {
        document.body.classList.remove('dark-mode');
      }
      
      // Force a re-render of Bootstrap components
      const event = new Event('themeChange');
      window.dispatchEvent(event);
    };
    
    // Initial update
    updateTheme();
    
    // Listen for system preference changes
    mediaQuery.addEventListener('change', updateTheme);
    
    // Cleanup
    return () => {
      mediaQuery.removeEventListener('change', updateTheme);
    };
  }, [themeMode]);
  
  // Save theme mode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
  }, [themeMode]);

  // Change theme mode
  const changeThemeMode = (mode) => {
    if (Object.values(THEME_MODE).includes(mode)) {
      setThemeMode(mode);
    }
  };

  // Context value
  const value = {
    themeMode,
    isDarkMode,
    changeThemeMode,
    THEME_MODE
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
